import { BreadcrumbsData, Hint, NeutralButton, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest, PayloadTooLarge } from '@systemeio/util'
import { SaveAndSendNewsletterModal } from 'modules/newsletters/single/components/save-and-send-newsletter-modal'
import { ScheduleNewsletterModal } from 'modules/newsletters/single/components/schedule-newsletter-modal'
import { newsletterDefaultErrors } from 'modules/newsletters/single/constants/newsletter-default-errors'
import { useGetNewsletterView } from 'modules/newsletters/single/hooks/use-get-newsletter-view'
import { useNewsletterContactsSettings } from 'modules/newsletters/single/hooks/use-newsletter-contacts-settings'
import { useUpdateNewsletter } from 'modules/newsletters/single/hooks/use-update-newsletter'
import { NewsletterId } from 'modules/newsletters/single/types/newsletter-interface'
import { NewsletterSplitTestViewApiInterface } from 'modules/newsletters/single/types/newsletter-view-interface'
import { getNewslettersListNextjsUrl } from 'modules/newsletters/single/utils/get-newsletters-list-nextjs-url'
import { setNewsletterEmailErrors } from 'modules/newsletters/single/utils/set-newsletter-email-errors'
import { CancelSplitTestModal } from 'modules/newsletters/split-test/components/cancel-split-test-modal'
import { InterruptSplitTestWithButton } from 'modules/newsletters/split-test/components/interrupt-split-test'
import { SaveAndTestSplitTest } from 'modules/newsletters/split-test/components/save-and-test-split-test'
import { SplitTestEditorBlock } from 'modules/newsletters/split-test/components/split-test-editor-block'
import { SplitTestSettings } from 'modules/newsletters/split-test/components/split-test-settings'
import { VariantsTabs } from 'modules/newsletters/split-test/components/variants-tabs'
import { defaultNewsletterSplitTestSettingsErrors } from 'modules/newsletters/split-test/constants/default-settings-errors'
import { MailingSplitTestStatusTypeEnum } from 'modules/newsletters/split-test/enum/mailing-split-test-status-type-enum'
import { useSplitTestSettings } from 'modules/newsletters/split-test/hooks/use-split-test-seetings'
import { useUpdateNewsletterSplitTestSettings } from 'modules/newsletters/split-test/hooks/use-update-newsletter-split-test-settings'
import { NewsletterSplitTestErrorResponseContentInterface } from 'modules/newsletters/split-test/types/newsletter-split-test-interface'
import { getSplitTestNewsletterStatisticsUrl } from 'modules/newsletters/split-test/utils/get-split-test-newsletter-statistics-url'
import { transformToNewsletterWithSplitTestData } from 'modules/newsletters/split-test/utils/transform-to-newsletter-with-split-test-data'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import { ButtonWithDropdown } from 'shared/components/buttin-with-dropdown/button-with-dropdown'
import { ButtonWithDropdownTypeEnum } from 'shared/components/buttin-with-dropdown/enums/button-with-dropdown-type-enum'
import AttachmentsBlock from 'shared/components/dashboard-mail-editor/components/attachments-block'
import ContactsSettingsBlock from 'shared/components/dashboard-mail-editor/components/contacts-settings-block'
import EmailSettingsBlock from 'shared/components/dashboard-mail-editor/components/email-settings-block'
import SubstitutesBlock from 'shared/components/dashboard-mail-editor/components/substitutes-block'
import { useMailingState } from 'shared/components/dashboard-mail-editor/hooks/use-mailing-state'
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email'
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data'
import { validateEmailBeforeSave } from 'shared/components/dashboard-mail-editor/utils/validate-email-before-save'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { deepCompare } from 'shared/utils/deepCompare'
import {
  CloseIcon,
  EventIcon,
  SendEmailIcon,
  StatisticsIcon,
  TestTubeIcon,
} from '@systemeio/ui/icons'

interface NewsletterSplitTestProps {
  newsletterId: NewsletterId
  newsletterWithSplitTest: NewsletterSplitTestViewApiInterface
}

export const NewsletterSplitTest = ({
  newsletterId,
  newsletterWithSplitTest,
}: NewsletterSplitTestProps) => {
  const { t } = useLocoTranslation()
  const { isRestrictedUser } = useUser()

  const router = useRouter()

  const [isFetching, setIsFetching] = useState<boolean>(false)

  const selectedVariantId = router.query.variant
    ? Number(router.query.variant)
    : newsletterWithSplitTest.mailingSplitTest.mailingVariantA.id

  const variantA: ReturnType<typeof useMailingState> = useMailingState({
    mailing: newsletterWithSplitTest.mailingSplitTest.mailingVariantA,
    onSave: data =>
      onSave({
        variantA: { ...variantA, tempState: data },
        variantB,
        options: { throwError: true },
      }),
    defaultErrors: newsletterDefaultErrors,
  })

  const variantB: ReturnType<typeof useMailingState> = useMailingState({
    mailing: newsletterWithSplitTest.mailingSplitTest.mailingVariantB,
    onSave: data =>
      onSave({
        variantA,
        variantB: { ...variantB, tempState: data },
        options: { throwError: true },
      }),
    defaultErrors: newsletterDefaultErrors,
  })

  const isLoading =
    isFetching || variantA.isFetchingChooseEmailTemplate || variantB.isFetchingChooseEmailTemplate

  const [commonErrors, setCommonErrors] = useState<string[]>([])

  const [settingsErrors, setSettingsErrors] = useState(defaultNewsletterSplitTestSettingsErrors)

  const selectedVariant = selectedVariantId === variantA.mailing.id ? variantA : variantB

  const { contactsSettings, setContactsSettings } =
    useNewsletterContactsSettings(newsletterWithSplitTest)

  const { splitTestSettings, setSplitTestSettings } = useSplitTestSettings(newsletterWithSplitTest)

  const { mutateNewslettersData } = useGetNewsletterView({
    newsletterId,
  })

  const { fetcherUpdateNewsletter } =
    useUpdateNewsletter<NewsletterSplitTestViewApiInterface>(newsletterId)
  const { fetcherUpdateNewsletterSplitTestSettings } =
    useUpdateNewsletterSplitTestSettings(newsletterId)

  const breadcrumbsData: BreadcrumbsData[] = [
    {
      caption: t('dashboard.newsletter.newsletters'),
      href: getNewslettersListNextjsUrl(),
      isStatic: true,
    },
    {
      caption: t('dashboard.newsletter.split_test.title'),
      isStatic: true,
    },
  ]

  const unsavedDataVariantA = useUnsavedEmail({
    mailingId: newsletterWithSplitTest.mailingSplitTest.mailingVariantA.id,
    currentBody: variantA.tempState.body,
    initialBody: newsletterWithSplitTest.mailingSplitTest.mailingVariantA.currentEmail.body,
    showImmediately: true,
    onPaste: data => variantA.setTempState(prev => ({ ...prev, body: data })),
  })

  const unsavedDataVariantB = useUnsavedEmail({
    mailingId: newsletterWithSplitTest.mailingSplitTest.mailingVariantB.id,
    currentBody: variantB.tempState.body,
    initialBody: newsletterWithSplitTest.mailingSplitTest.mailingVariantB.currentEmail.body,
    showImmediately: true,
    onPaste: data => variantB.setTempState(prev => ({ ...prev, body: data })),
  })

  const { tags: listOfTags } = useTagsAll()

  const [isDataSaved, setIsDataSaved] = useState(true)

  useEffect(() => {
    setIsDataSaved(
      deepCompare(
        transformToNewsletterWithSplitTestData({
          listOfTags: listOfTags,
          variantA,
          variantB,
          contactsSettings,
          newsletterData: newsletterWithSplitTest,
          splitTestSettings,
          attachmentsTempStateVariantA: variantA.attachmentsTempState,
          attachmentsTempStateVariantB: variantB.attachmentsTempState,
        }),
        newsletterWithSplitTest,
      ),
    )
  }, [contactsSettings, listOfTags, newsletterWithSplitTest, splitTestSettings, variantA, variantB])

  const onSave = async ({
    variantA,
    variantB,
    options,
  }: {
    variantA: ReturnType<typeof useMailingState>
    variantB: ReturnType<typeof useMailingState>
    options?: { throwError?: boolean }
  }) => {
    try {
      variantA.setDefaultErrors()
      variantB.setDefaultErrors()
      setCommonErrors([])
      setSettingsErrors(defaultNewsletterSplitTestSettingsErrors)

      validateEmailBeforeSave(variantA.tempState.fromEmail, variantA.setErrors, t)
      validateEmailBeforeSave(variantB.tempState.fromEmail, variantB.setErrors, t)

      setIsFetching(true)

      const [_, newsletterSplitTestResponseData] = await Promise.all([
        fetcherUpdateNewsletter({
          tags: contactsSettings.tags.included,
          excludedTags: contactsSettings.tags.excluded,
          daysExists: contactsSettings.daysExists,
        }),
        fetcherUpdateNewsletterSplitTestSettings({
          settings: splitTestSettings,
          mailingVariantA: {
            currentEmail: getMailingUpdateData(variantA.tempState, variantA.attachmentsTempState),
          },
          mailingVariantB: {
            currentEmail: getMailingUpdateData(variantB.tempState, variantB.attachmentsTempState),
          },
        }),
      ])

      await mutateNewslettersData(
        () =>
          transformToNewsletterWithSplitTestData({
            listOfTags,
            newsletterData: newsletterWithSplitTest,
            splitTestSettings,
            variantA,
            variantB,
            contactsSettings,
            attachmentsTempStateVariantA:
              newsletterSplitTestResponseData.mailingVariantA.currentEmail.attachments,
            attachmentsTempStateVariantB:
              newsletterSplitTestResponseData.mailingVariantB.currentEmail.attachments,
          }),
        false,
      )

      toast.success(t('global.changes_saved'))
      setIsFetching(false)
      await unsavedDataVariantA.removeDbRecord()
      await unsavedDataVariantB.removeDbRecord()
    } catch (e) {
      setIsFetching(false)
      toast.error(t('global.error'))
      if (e instanceof BadRequest) {
        const errors = e.errors as NewsletterSplitTestErrorResponseContentInterface

        if (errors.fields) {
          const { settings, mailingVariantA, mailingVariantB } = errors.fields
          if (settings) {
            setSettingsErrors(settings)
          }
          if (mailingVariantA) {
            setNewsletterEmailErrors(variantA.setErrors, mailingVariantA.currentEmail)
          }
          if (mailingVariantB) {
            setNewsletterEmailErrors(variantB.setErrors, mailingVariantB.currentEmail)
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common
          setCommonErrors(commonErrors)
        }
      } else if (e instanceof PayloadTooLarge) {
        toast.error(t('core.error.payload_to_large_error_message'))
      }
      if (options?.throwError) {
        throw e
      }
    }
  }

  const isTagsSelected = !!contactsSettings.tags.included.length

  const isRunning =
    newsletterWithSplitTest.mailingSplitTest.status === MailingSplitTestStatusTypeEnum.Running

  const interruptSplitTest = async () => {
    await mutateNewslettersData(
      data =>
        data && {
          ...data,
          mailingSplitTest: data.mailingSplitTest
            ? {
                ...data.mailingSplitTest,
                status: MailingSplitTestStatusTypeEnum.Interrupted,
              }
            : null,
        },
      false,
    )
  }

  return (
    <>
      <div className={`flex flex-col gap-5`}>
        <div className="flex flex-col items-center justify-between gap-5 sm:flex-row">
          <BreadcrumbsDashboard data={breadcrumbsData} />
          <div className={'flex gap-3'}>
            {newsletterWithSplitTest?.sent ? (
              <div className={'flex gap-5'}>
                {isRunning && (
                  <InterruptSplitTestWithButton
                    mailingSplitTestId={newsletterWithSplitTest.mailingSplitTest.id}
                    afterAbandon={interruptSplitTest}
                  />
                )}
                <PrimaryButton
                  as={LinkWithoutPrefetch}
                  aria-disabled={isLoading}
                  href={getSplitTestNewsletterStatisticsUrl(newsletterId)}
                >
                  <StatisticsIcon className={'h-[20px] w-[20px]'} />
                  <span className="hidden lg:flex">
                    {t('dashboard.newsletter_editor.statistics')}
                  </span>
                </PrimaryButton>
              </div>
            ) : (
              <>
                <CancelSplitTestModal
                  newsletterId={newsletterId}
                  mailingSplitTest={newsletterWithSplitTest.mailingSplitTest}
                />
                <ButtonWithDropdown
                  options={[
                    {
                      name: 'save-and-test-variant',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.save_and_test'),
                        icon: <TestTubeIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <SaveAndTestSplitTest
                            onSave={() =>
                              onSave({ variantA, variantB, options: { throwError: true } })
                            }
                            opened={opened}
                            onClose={onClose}
                            key={key}
                            variantA={variantA}
                            variantB={variantB}
                          />
                        )
                      },
                    },
                    {
                      name: 'save-and-run-split-test',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.split_test.save_and_run.title'),
                        icon: <SendEmailIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <SaveAndSendNewsletterModal
                            opened={opened}
                            onClose={onClose}
                            key={key}
                            newsletterId={newsletterId}
                            onSave={() =>
                              onSave({ variantA, variantB, options: { throwError: true } })
                            }
                            confirmationText={t(
                              'dashboard.newsletter_editor.split_test.save_and_run.description',
                            )}
                            isTagsSelected={isTagsSelected}
                          />
                        )
                      },
                    },
                    {
                      name: 'schedule-split-test',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.save_and_schedule'),
                        icon: <EventIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <ScheduleNewsletterModal
                            title={t('dashboard.newsletter_editor.split_test.schedule_split_test')}
                            opened={opened}
                            isTagsSelected={isTagsSelected}
                            onClose={onClose}
                            key={key}
                            scheduledAt={newsletterWithSplitTest.scheduledAt}
                            newsletterId={newsletterId}
                            onSave={() =>
                              onSave({ variantA, variantB, options: { throwError: true } })
                            }
                            mutateNewslettersData={mutateNewslettersData}
                          />
                        )
                      },
                    },
                  ]}
                  disabled={isLoading}
                  onClick={() => onSave({ variantA, variantB })}
                  title={t('global.save_draft')}
                  isFetching={isFetching}
                />
              </>
            )}
            <NeutralButton as={LinkWithoutPrefetch} href={getNewslettersListNextjsUrl()}>
              <CloseIcon className={'h-[20px] w-[20px]'} />
              <span className="hidden lg:flex">{t('global.exit')}</span>
            </NeutralButton>
          </div>
        </div>
        <div className={'flex flex-col gap-3'}>
          {commonErrors.map((error, index) => (
            <p className="text-danger px-6 text-center text-sm" key={index}>
              {error}
            </p>
          ))}
          <div className={'flex flex-col gap-5 lg:flex-row'}>
            <div className={`flex min-w-0 flex-col gap-5 lg:h-auto lg:basis-3/4`}>
              <Hint
                label={t('dashboard.newsletter_editor.split_test.general_settings.description')}
              >
                <b className={'text-text-primary text-base'}>
                  {t('dashboard.newsletter_editor.split_test.general_settings.title')}
                </b>
              </Hint>
              <SplitTestSettings
                disabled={newsletterWithSplitTest.sent}
                isPreFetching={isLoading}
                errors={settingsErrors}
                splitTestSettings={splitTestSettings}
                onChange={setSplitTestSettings}
              />
              <ContactsSettingsBlock
                disabled={newsletterWithSplitTest.sent}
                isPreFetching={isLoading}
                contactsSettings={contactsSettings}
                onChangeContactsSettings={setContactsSettings}
              />
              <div>
                <VariantsTabs
                  variantAHasError={variantA.hasErrors}
                  variantBHasError={variantB.hasErrors}
                  splitTestData={newsletterWithSplitTest.mailingSplitTest}
                />
                <SplitTestEditorBlock
                  variantA={variantA}
                  variantB={variantB}
                  unsavedDataVariantA={unsavedDataVariantA}
                  unsavedDataVariantB={unsavedDataVariantB}
                  isLoading={isLoading}
                  disabled={newsletterWithSplitTest.sent}
                  selectedVariantId={selectedVariantId}
                />
              </div>
            </div>
            <div
              className={`static top-5 flex h-fit flex-col gap-5 sm:min-w-[320px] lg:sticky lg:basis-1/4`}
            >
              <Hint
                label={t('dashboard.newsletter_editor.split_test.specific_settings.description')}
              >
                <b className={'text-text-primary text-base'}>
                  {t('dashboard.newsletter_editor.split_test.specific_settings.title')}
                </b>
              </Hint>
              <EmailSettingsBlock
                disabled={newsletterWithSplitTest.sent}
                isPreFetching={isLoading}
                tempState={selectedVariant.tempState}
                onChangeEmail={selectedVariant.onChangeEmail}
                errors={selectedVariant.errors}
              />

              <AttachmentsBlock
                disabled={newsletterWithSplitTest.sent}
                isPreFetching={isLoading}
                attachmentsTempState={selectedVariant.attachmentsTempState}
                setAttachmentsTempState={selectedVariant.setAttachmentsTempState}
                errors={selectedVariant.errors?.attachments}
              />
              <SubstitutesBlock />
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.newsletter_editor.confirmation_message_before_exit')}
        hideModal={isDataSaved || newsletterWithSplitTest.sent}
        shouldSkipOnRouteChange={(route: string) => {
          const newPath = route.split('?')[0]
          const existingPath = (router.basePath + router.asPath).split('?')[0]
          return newPath === existingPath
        }}
      />
    </>
  )
}
